import { NON_FIELD_ERRORS, UNHANDLED_ERROR } from 'consts';
import { cloneDeep } from 'lodash';
import * as Yup from 'yup';
import { isBefore, isValid, parse } from 'date-fns';

// Needed to init yup, cause of webpack import error
import 'helpers/yup';

export const formatErrors = (
  errorArg,
  { fieldsToNonField = [], transformError } = {},
) => {
  let error = errorArg;
  if (!error.response) {
    error = {
      response: error,
      message: errorArg.message,
    };
  }
  const { message, response: { data: { errors } = {} } = {} } = error;

  const result = {
    fieldErrors: [],
    nonFieldErrors: [],
  };

  const fieldsToNonFieldHash = { __all__: 1 };
  fieldsToNonField.forEach((fld) => {
    fieldsToNonFieldHash[fld] = 1;
  });

  if (errors) {
    errors.forEach((eArg) => {
      let e = eArg;

      if (transformError) {
        e = transformError(e);
      }

      if (!!fieldsToNonFieldHash[e.field]) {
        let msg = e.message;
        if (e.field !== '__all__') {
          msg = `${e.field}: ${msg}`;
        }
        result.nonFieldErrors.push({
          ...e,
          message: msg,
        });
        return;
      }

      if (e.field === NON_FIELD_ERRORS) {
        result.nonFieldErrors.push(e);
        return;
      }

      result.fieldErrors.push(e);
    });

    return result;
  }

  if (message && !result.nonFieldErrors?.length) {
    result.nonFieldErrors.push({
      code: UNHANDLED_ERROR,
      field: NON_FIELD_ERRORS,
      message,
    });
  }

  return result;
};

/**
 * Password dificulty
 */
export const passwordValidationSchema = Yup.string()
  .min(8)
  .max(24)
  .includesUppercase()
  .includesLowercase()
  .includesDigit()
  .includesSpecialChar()
  .required()
  .label('Password');

export const passwordRules = [
  {
    type: 'minMax',
    label: 'Must have 8-24 characters',
  },
  {
    type: 'includesUppercase',
    label: 'Includes an uppercase',
  },
  {
    type: 'includesLowercase',
    label: 'Includes a lowercase',
  },
  {
    type: 'includesDigit',
    label: 'Includes a number',
  },
  {
    type: 'includesSpecialChar',
    label: 'Includes a special character ($, !, @, &, *, /)',
  },
];

export const getPasswordErrorsTypes = (errors) => {
  const errorTypes = {};
  errors?.forEach?.(({ type }) => {
    if (type === 'min' || type === 'max') {
      errorTypes.minMax = true;
      return;
    }

    errorTypes[type] = true;
  });

  return errorTypes;
};

export const getPasswordDifficultyLevel = (errorsTypesLength) => {
  if (errorsTypesLength === 0) {
    return 3;
  }

  if (errorsTypesLength <= Math.floor(passwordRules.length / 2)) {
    return 2;
  }

  if (errorsTypesLength - 1 === passwordRules.length) {
    return 0;
  }

  return 1;
};

/**
 * Personal info
 */
export const personalInfoSchemaObj = {
  firstName: Yup.string()
    .min(2)
    .max(140)
    .onlyLetters()
    .required()
    .label('First name'),
  lastName: Yup.string()
    .min(2)
    .max(140)
    .onlyLetters()
    .required()
    .label('Last name'),
  company: Yup.string().min(2).required().label('Company name'),
  email: Yup.string().email().required(),
  phone: Yup.string().phone(),
  businessPhone: Yup.string().phone(),
  addressLine1: Yup.object().nullable().required(),
  locality: Yup.object().nullable().required().label('City'),
  region: Yup.object().nullable().required().label('State'),
  postcode: Yup.string().postcode().required().label('Zip code'),
};

const billingPersonalInfoSchemaObj = cloneDeep(personalInfoSchemaObj);
delete billingPersonalInfoSchemaObj.company;
delete billingPersonalInfoSchemaObj.email;
delete billingPersonalInfoSchemaObj.phone;

export { billingPersonalInfoSchemaObj };

/**
 * Credit card
 */
export const creditCardSchemaObj = {
  cardholderName: Yup.string()
    .onlyLetters()
    .required()
    .label('Cardholder name'),
  cardNumber: Yup.string()
    .label('Card number')
    .when('id', {
      is: (exists) => !exists,
      then: (schema) =>
        schema.min(18, 'Card number must be min 15 digits').max(19).required(),
    }),
  expirationMonth: Yup.string()
    .label('Expiration month')
    .when('id', {
      is: (exists) => !exists,
      then: (schema) => schema.required(),
    }),
  expirationYear: Yup.string()
    .label('Expiration year')
    .when('id', {
      is: (exists) => !exists,
      then: (schema) => schema.length(4).required(),
    }),
  cvv: Yup.string()
    .label('CVV')
    .when('id', {
      is: (exists) => !exists,
      then: (schema) => schema.min(3).max(4).required(),
    }),
  id: Yup.number(),
};

export const consentSchemaObj = {
  consent: Yup.boolean()
    .isTrue('Please agree to the terms and conditions to continue')
    .required()
    .label('Consent'),
};

const validateTime = (time, context, dayPartingObj) => {
  if (dayPartingObj?.allDay || !time) return true;
  const elementIndex = context.parent.index;
  const currentDay = dayPartingObj?.days.find(
    (day) => day.index === elementIndex,
  );
  if (!currentDay || currentDay.allDay) return true;
  const { startTime, endTime } = currentDay;
  if (startTime && endTime) {
    const startDate = parse(startTime, 'HH:mm', new Date());
    const endDate = parse(endTime, 'HH:mm', new Date());

    return (
      isValid(startDate) && isValid(endDate) && isBefore(startDate, endDate)
    );
  }
  const date = parse(time, 'HH:mm', new Date());
  return isValid(date);
};

export const dayPartingSchema = Yup.object({
  dayParting: Yup.lazy((value) => {
    return Yup.object({
      allDay: Yup.boolean(),
      days: Yup.array()
        .of(
          Yup.object({
            allDay: Yup.boolean(),
            startTime: Yup.string().test(
              'Start Time',
              '*Start Time',
              (time, context) => validateTime(time, context, value),
            ),
            endTime: Yup.string().test(
              'End Time',
              '*End Time',
              (time, context) => validateTime(time, context, value),
            ),
            weekday: Yup.array().of(Yup.string()).unique(),
            priority: Yup.string(),
          }),
        )
        .unique((a) => a.weekday.join(', '), 'duplicate weekdays')
        .compact(({ startTime, endTime, allDay }) => {
          return (!startTime || !endTime) && !allDay;
        })
        .min(value.allDay ? 0 : 1),
    });
  }),
});
